import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { fmtCurrency, fmtCurrencyK } from "../lib/formatters";
import { useHoverIntent } from "../lib/DelayedTooltip";
import { useAutoDismiss } from "../hooks/useAutoDismiss";

// Click-toggle horizontal bar with a per-element breakdown popover.
// -----------------------------------------------------------------------
// The popover is portaled to `document.body` with `position: fixed` so
// it escapes the Elements panel's `overflow: hidden` and can spill below
// the panel edge without being clipped. On open we measure the bar's
// bounding rect + the popover's rendered size and pick a position that
// stays fully inside the viewport (auto-flips up if there isn't enough
// room below, and clamps horizontally so it doesn't fall off the right).
function BarWithDetail({ label, value, pct, color, testid, elements, costByYear, yearIdx }) {
  const [open, setOpen] = useState(false);
  const [placed, setPlaced] = useState({ left: 0, top: 0, opacity: 0 });
  const barRef = useRef(null);
  const popRef = useRef(null);
  const rowRef = useRef(null);

  // 2026-02-19 (Dave) — auto-dismiss the click-open detail popover
  // after `hover_visible_seconds` to match the rest of the Dashboard.
  const closePop = useCallback(() => setOpen(false), []);
  const hover = useHoverIntent();
  useAutoDismiss(open, closePop);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      const insidePop = popRef.current?.contains(e.target);
      const insideBar = barRef.current?.contains(e.target);
      if (!insidePop && !insideBar) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  // Measure bar + popover once mounted and position the popover in the
  // viewport. Preferred: directly below the bar, aligned left. Flips
  // above if it would run off the bottom.
  useLayoutEffect(() => {
    if (!open || !popRef.current || !barRef.current) return;
    const barRect = barRef.current.getBoundingClientRect();
    const popRect = popRef.current.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    let left = barRect.left;
    let top = barRect.bottom + 4;
    if (top + popRect.height > vh - 8) {
      // Not enough room below — flip above.
      top = Math.max(8, barRect.top - popRect.height - 4);
    }
    left = Math.max(8, Math.min(left, vw - popRect.width - 8));
    setPlaced({ left, top, opacity: 1 });
  }, [open]);

  const rows = [...elements]
    .map((e) => ({
      name: e.name,
      v: yearIdx == null ? e.extended_cost || 0 : (costByYear?.[e.element_id]?.[yearIdx] ?? (e.extended_cost || 0)),
    }))
    .sort((a, b) => b.v - a.v);

  return (
    <div className="mb-3 relative" ref={rowRef}>
      <div className="flex items-baseline justify-between text-[10.5px] mb-0.5">
        <span className="tracking-[0.1em] uppercase font-semibold text-slate">{label}</span>
        <span
          className="font-mono tabular-nums text-ink font-semibold"
          data-testid={`${testid}-value`}
        >
          {fmtCurrencyK(value)}
        </span>
      </div>
      <button
        type="button"
        ref={barRef}
        onClick={() => { hover.cancel(); setOpen((v) => !v); }}
        onMouseEnter={() => hover.start(() => setOpen(true))}
        onMouseLeave={hover.cancel}
        /* 2026-02-19 (Dave) — narrower bars (h-6 → h-5) so the two rows
           read less prominent; hover tint replaces the previous floating
           dark tooltip to match the Waterfall/Reserve Activity pattern.
           `group` enables the child overlay to react to hover. */
        className="w-full h-5 bg-navy-wash relative block group"
        data-testid={testid}
        style={{ cursor: "pointer", padding: 0, border: 0 }}
      >
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
        {/* Tinted hover-hint overlay — mirrors the Waterfall/Activity
            chart's `rgba(28, 78, 128, 0.06)` treatment. Signals
            clickability without a floating tooltip. */}
        <div
          className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: "rgba(28, 78, 128, 0.06)" }}
          aria-hidden="true"
        />
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          data-testid={`${testid}-popover`}
          className="bg-white shadow-lg"
          style={{
            position: "fixed",
            left: placed.left,
            top: placed.top,
            opacity: placed.opacity,
            zIndex: 60,
            width: 340,
            maxHeight: 320,
            overflow: "hidden",
            border: "1px solid #1C4E80",
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            transition: "opacity 120ms ease-in",
          }}
        >
          <div className="bg-navy-wash border-b border-rule">
            <div className="px-2.5 py-1.5 flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="font-display font-semibold text-navy" style={{ fontSize: 12.5, letterSpacing: 0 }}>
                    {label}
                  </div>
                  {/* 2026-02-19 (Dave) — Total baseline-aligned with the
                      title, right-side of the label column so it locks
                      to the title row rather than centering against the
                      combined title+subtitle block. */}
                  <div
                    className="font-mono tabular-nums"
                    style={{ fontWeight: 600, color: "#1C4E80", fontSize: 11 }}
                    data-testid={`${testid}-popover-total`}
                  >
                    {fmtCurrency(value)}
                  </div>
                </div>
                <div className="text-slate" style={{ fontSize: 10.5 }}>
                  {elements.length} elements sorted by extended cost
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                className="text-slate hover:text-navy px-1 self-start"
                data-testid={`${testid}-popover-close`}
              >
                ×
              </button>
            </div>
          </div>
          <div style={{ overflowY: "auto", maxHeight: 220 }}>
            <table className="w-full" style={{ fontFamily: "IBM Plex Mono", fontSize: 10.5 }}>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-rule">
                    <td
                      className="px-2 py-0.5 text-ink"
                      style={{
                        fontFamily: "Source Sans 3",
                        maxWidth: 210,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.name}
                    </td>
                    <td className="px-2 py-0.5 text-right tabular-nums text-ink">
                      {fmtCurrency(r.v)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Two horizontal bars comparing today's cost vs the inflated cost at the
// end of the currently-selected horizon (5/10/15/20/25/30). Hovering
// either bar reveals a per-element breakdown popover.
export default function ElementsCostBars({ association, variables, calc, horizon = 30 }) {
  if (!association || !variables) return null;
  const els = association.elements || [];
  // Totals by year are server-computed (calc.cost_total_exact_by_year).
  const totals = calc?.cost_total_exact_by_year || [];
  const current = totals[0] ?? 0;
  const H = Math.max(1, Math.min(horizon, totals.length || 1));
  const futureYear = association.study_year + H - 1;
  const future = totals[H - 1] ?? current;
  const axisMax = totals[totals.length - 1] ?? current;
  const pctCurrent = axisMax ? (current / axisMax) * 100 : 0;
  const pctFuture = axisMax ? (future / axisMax) * 100 : 0;
  const futureColor = "#5379A0";

  return (
    <div className="panel-compact flex flex-col" data-testid="elements-cost-panel">
      <div className="dash-panel-header">
        <div className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy">
          Elements ({els.length})
        </div>
        <div
          className="text-[10.5px] text-navy font-semibold font-mono tabular-nums"
          data-testid="elements-horizon-label"
        >
          {horizon} Year Horizon
        </div>
      </div>

      {/* Vertically-centered bars — fills remaining height so the two-bar
          block sits in the visual center of the panel. */}
      <div className="flex-1 flex flex-col justify-center gap-6 min-h-0">
        <BarWithDetail
          /* 2026-02-16 (Dave) — label reordered "2026 Current Cost"
             → "Current Cost 2026" so the metric leads the year. */
          label={`Current Cost ${association.study_year}`}
          value={current}
          pct={pctCurrent}
          /* 2026-02-16 (Dave) — bumped from #C9D6E2 (very light) to
             a mid-tone sky (#A5BAD0) so the current-cost bar stands
             out with more contrast against the white panel while
             remaining visibly lighter than the future-cost bar. */
          color="#A5BAD0"
          testid="elements-bar-current"
          elements={els}
          costByYear={calc?.display?.element_cost_by_year}
          yearIdx={null}
        />
        <BarWithDetail
          label={`${futureYear} Cost`}
          value={future}
          pct={pctFuture}
          color={futureColor}
          testid="elements-bar-future"
          elements={els}
          costByYear={calc?.display?.element_cost_by_year}
          yearIdx={H - 1}
        />
      </div>
    </div>
  );
}
